import React, { useState, useEffect } from "react";
import "./ContentPage.css";
import { apiGet, apiPost, apiPut, apiDelete } from "../../services/api";
import { Content } from "../../types";
import { SidePanel } from "../shared/SidePanel";
import { FormField, Button } from "../shared/FormField";

export function ContentPage() {
  const [items, setItems] = useState<Content[]>([]);
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState<"upload" | "ai">("upload");
  const [saving, setSaving] = useState(false);

  const [uploadForm, setUploadForm] = useState({ title: "", description: "", tags: "", file: null as File | null });
  const [aiForm, setAiForm] = useState({ sourceMaterial: "", contentType: "battlecard" });

  useEffect(() => { loadContent(); }, []);

  const loadContent = async () => {
    try {
      const result = await apiGet<{ items: Content[] }>("/api/content");
      setItems(result.items);
    } catch (err) {
      console.error("Failed to load content:", err);
    } finally {
      setLoading(false);
    }
  };

  const openUpload = () => { setPanelMode("upload"); setUploadForm({ title: "", description: "", tags: "", file: null }); setPanelOpen(true); };
  const openAI = () => { setPanelMode("ai"); setAiForm({ sourceMaterial: "", contentType: "battlecard" }); setPanelOpen(true); };

  const handleUpload = async () => {
    setSaving(true);
    try {
      let mediaUrl = "";
      if (uploadForm.file) {
        const { uploadUrl, contentId, s3Key } = await apiPost<{ uploadUrl: string; contentId: string; s3Key: string }>(
          "/api/content/upload-url",
          { filename: uploadForm.file.name, contentType: uploadForm.file.type },
        );
        await fetch(uploadUrl, { method: "PUT", body: uploadForm.file, headers: { "Content-Type": uploadForm.file.type } });
        mediaUrl = s3Key;

        await apiPost("/api/content", {
          contentId,
          title: uploadForm.title,
          description: uploadForm.description,
          type: "video",
          mediaUrl,
          tags: uploadForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
        });
      } else {
        await apiPost("/api/content", {
          title: uploadForm.title,
          description: uploadForm.description,
          type: "video",
          tags: uploadForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
        });
      }
      setPanelOpen(false);
      await loadContent();
    } catch (err) {
      console.error("Failed to upload content:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleAIGenerate = async () => {
    setSaving(true);
    try {
      await apiPost("/api/content/generate", { sourceMaterial: aiForm.sourceMaterial, contentType: aiForm.contentType });
      setPanelOpen(false);
      await loadContent();
    } catch (err) {
      console.error("Failed to generate AI content:", err);
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (item: Content) => {
    const newStatus = item.status === "published" ? "unpublished" : "published";
    try {
      await apiPut(`/api/content/${item.contentId}`, { status: newStatus });
      await loadContent();
    } catch (err) {
      console.error("Failed to update content:", err);
    }
  };

  const handleDeleteContent = async (id: string) => {
    if (!confirm("Delete this content?")) return;
    try {
      await apiDelete(`/api/content/${id}`);
      await loadContent();
    } catch (err) {
      console.error("Failed to delete content:", err);
    }
  };

  if (loading) return <div className="protrain-admin-loading">Loading content...</div>;

  return (
    <div className="protrain-content-page">
      <div className="protrain-admin-header">
        <h2 className="protrain-page-title">Content Management</h2>
        <div className="protrain-content-actions">
          <Button variant="secondary" onClick={openUpload}>📤 Upload Content</Button>
          <Button onClick={openAI}>✨ AI Generate</Button>
        </div>
      </div>

      <table className="protrain-content-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Type</th>
            <th>Tags</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.contentId}>
              <td className="protrain-content-table-title">{item.title}</td>
              <td><span className="protrain-content-type-badge">{item.type === "ai_generated" ? "🤖 AI" : "📹 Video"}</span></td>
              <td>
                <div className="protrain-content-tags">
                  {item.tags.map((t) => <span key={t} className="protrain-content-tag">{t}</span>)}
                </div>
              </td>
              <td>
                <span className={`protrain-content-status protrain-content-status--${item.status}`}>{item.status}</span>
              </td>
              <td className="protrain-content-date">{new Date(item.createdAt).toLocaleDateString()}</td>
              <td>
                <div className="protrain-content-row-actions">
                  <button className="protrain-content-action-btn" onClick={() => togglePublish(item)}>
                    {item.status === "published" ? "Unpublish" : "Publish"}
                  </button>
                  <button className="protrain-content-action-btn protrain-content-action-btn--danger" onClick={() => handleDeleteContent(item.contentId)}>
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr><td colSpan={6} className="protrain-admin-empty">No content yet.</td></tr>
          )}
        </tbody>
      </table>

      <SidePanel
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        title={panelMode === "upload" ? "Upload Content" : "AI Generate Content"}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPanelOpen(false)}>Cancel</Button>
            <Button onClick={panelMode === "upload" ? handleUpload : handleAIGenerate} disabled={saving}>
              {saving ? "Processing..." : panelMode === "upload" ? "Upload" : "✨ Generate"}
            </Button>
          </>
        }
      >
        {panelMode === "upload" ? (
          <>
            <FormField label="Title" htmlFor="content-title">
              <input id="content-title" value={uploadForm.title} onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })} />
            </FormField>
            <FormField label="Description" htmlFor="content-desc">
              <textarea id="content-desc" value={uploadForm.description} onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })} />
            </FormField>
            <FormField label="Tags (comma-separated)" htmlFor="content-tags">
              <input id="content-tags" value={uploadForm.tags} onChange={(e) => setUploadForm({ ...uploadForm, tags: e.target.value })} placeholder="competitor, product, objection" />
            </FormField>
            <FormField label="Video File">
              <input type="file" accept="video/*" onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null })} />
            </FormField>
          </>
        ) : (
          <>
            <FormField label="Source Material" htmlFor="ai-source">
              <textarea id="ai-source" value={aiForm.sourceMaterial} onChange={(e) => setAiForm({ ...aiForm, sourceMaterial: e.target.value })} placeholder="Paste product docs, competitive intel, or any source material..." style={{ minHeight: "200px" }} />
            </FormField>
            <FormField label="Content Type" htmlFor="ai-type">
              <select id="ai-type" value={aiForm.contentType} onChange={(e) => setAiForm({ ...aiForm, contentType: e.target.value })}>
                <option value="battlecard">Battlecard</option>
                <option value="tip">Quick Tip</option>
                <option value="objection_handler">Objection Handler</option>
              </select>
            </FormField>
          </>
        )}
      </SidePanel>
    </div>
  );
}
